export const MONTHS_IT = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

export const MONTHS_FULL = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

export const fmtDay = (d) => (d ? d.split("-").reverse().join("/") : "—");

export const POWER_UNITS = {
  kWh: { factor: 1, digits: 0 },
  MWh: { factor: 1e3, digits: 1 },
  GWh: { factor: 1e6, digits: 3 },
};

// Gas: conversione energetica con PCS standard 38,1 MJ/Smc.
export const PCS_STD = 10.5833;
export const GAS_UNITS = { Smc: { digits: 0 }, ...POWER_UNITS };
const EUR_PER_SMC = { "c€/mc": 0.01, "c€/smc": 0.01, "€/mc": 1, "€/smc": 1, "€/mwh": PCS_STD / 1000, "c€/kwh": PCS_STD / 100 };
export const toGasUnit = (smc, u) => (smc == null ? null : u === "Smc" ? smc : (smc * PCS_STD) / POWER_UNITS[u].factor);
export const fmtGas = (smc, u) => (smc == null ? "—" : fmtQty(toGasUnit(smc, u), GAS_UNITS[u].digits, u));
const priceFactor = (src) => EUR_PER_SMC[String(src ?? "").toLowerCase()];
export const gasPriceUnit = (u, src = "c€/Smc") => (u === "Smc" || priceFactor(src) == null ? src : u === "kWh" ? "c€/kWh" : "€/MWh");
export function toGasPrice(v, u, src = "c€/Smc") {
  const f = priceFactor(src);
  if (v == null || u === "Smc" || f == null) return v;
  return ((v * f) / PCS_STD) * (u === "kWh" ? 100 : 1000);
}

// Palette uniforme EA: Totale viola, Power ambra, Gas blu.
export const COLORS = { total: "#0140A6", power: "#8B32FB", gas: "#3285FD", totalLight: "#9DBBEF", powerLight: "#D3ABFD", gasLight: "#A6CBFD",
  totalDark: "#012F7A", powerDark: "#3F0C88", gasDark: "#0148BD",
  sustain: "#059669", sustainLight: "#A7F3D0", budget: "#94A3B8", forecast: "#071B4D", pending: "#CBD5E1" };

function num(v, digits) {
  return Number(v).toLocaleString("it-IT", { minimumFractionDigits: digits, maximumFractionDigits: digits, useGrouping: "always" });
}

export function fmtEur(v, digits = 0) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return `€ ${num(v, digits)}`;
}

export function fmtEurCompact(v) {
  if (v == null) return "";
  const a = Math.abs(v);
  if (a >= 1e6) return `€ ${num(v / 1e6, 2)} M`;
  if (a >= 1e3) return `€ ${num(v / 1e3, 0)} k`;
  return `€ ${num(v, 0)}`;
}

export function toPowerUnit(kwh, unit) {
  if (kwh == null) return null;
  return kwh / POWER_UNITS[unit].factor;
}

export function fmtPower(kwh, unit) {
  if (kwh == null) return "—";
  return `${num(toPowerUnit(kwh, unit), POWER_UNITS[unit].digits)} ${unit}`;
}

export function fmtQty(v, digits = 0, suffix = "") {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return `${num(v, digits)}${suffix ? ` ${suffix}` : ""}`;
}

export function fmtSignedPct(v, digits = 1) {
  if (v == null) return "—";
  return `${v > 0 ? "+" : ""}${num(v, digits)}%`;
}

export function gasLabel(p) {
  if (!p) return "—";
  if (p.point_type === "REMI") return `REMI ${p.identifier} · diretta rete Snam`;
  return `PDR ${p.identifier}${p.remi_code && p.remi_code !== p.identifier ? ` · REMI ${p.remi_code}` : ""}`;
}

export function monthsSpan(ms) {
  if (!ms?.length) return "—";
  const contiguous = ms.every((m, i) => i === 0 || m === ms[i - 1] + 1);
  return contiguous ? `${MONTHS_IT[ms[0] - 1]}–${MONTHS_IT[ms[ms.length - 1] - 1]}` : `${ms.length} mesi`;
}

// Messaggio standard per confronti senza dati: indica punto di fornitura e/o periodo mancante.
export function missingMsg(points, period) {
  if (points?.length) return `Non calcolabile: in database mancano i dati ${points.length > 1 ? "dei punti di fornitura" : "del punto di fornitura"} ${points.join(", ")} nel ${period}.`;
  return `Non calcolabile: in database mancano i dati del ${period}.`;
}

export const INDEX_NOTE = "Indice base 100: ogni valore è espresso in rapporto alla media della propria serie (100 = valore medio; 120 = +20% sopra la media, 80 = −20%). Serve a confrontare la forma dell'andamento (giorni lavorativi, weekend, ferie, stagionalità) anche quando i volumi sono molto diversi, ad esempio tra un periodo con più punti di fornitura e uno con meno, o tra uno stabilimento grande e uno piccolo.";

// Etichetta e indirizzo da Gestione clienti (se presenti).
export function pointSub(p) {
  return [p?.label, p?.address].filter(Boolean).join(" · ") || "—";
}

export function sectionOf(path) {
  if (path.includes("/kpi-sostenibilita/")) return "sustainability";
  return path.includes("/power/") ? "power" : path.includes("/gas/") ? "gas" : "overview";
}

export const SCOPES = [["company", "Totale aziendale"], ["site", "Siti"], ["point", "POD/PDR"]];
export const SCOPE_LABEL = { company: "Totale aziendale", site: "Sito", point: "POD/PDR" };

export function fmtMwh(mwh) {
  if (mwh == null) return "—";
  return Math.abs(mwh) >= 10000 ? fmtQty(mwh / 1000, 2, "GWh") : fmtQty(mwh, 1, "MWh");
}
